/* eslint-disable no-nested-ternary */
import { SummaryResponse } from '@/webapi/use-analytics-api';
import { MetricKind } from '@/webapi/models';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { CONTROL } from '@/features/details/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { maybe } from '@/features/details/utils';

export const getFormattedSingleMetric = (
  summary: SummaryResponse,
  metric: MetricKind,
  variant: string,
): string => formatNumber(getSingleMetric(summary, metric, variant));

export function getUplift(
  data: SummaryResponse,
  metric: MetricKind,
  _variants: Array<ExperienceVariant>,
  variantId: string,
): number {
  const variants: Array<ExperienceVariant> = _variants && clone(_variants);

  const control = variants.find((v) => v.id === data.controlVariationId);
  const variation = variants.find((v) => v.id === variantId);

  if (metric === MetricKind.TRANSACTIONS) {
    metric = MetricKind.CONVERSION_RATE;
  } else if (metric === MetricKind.REVENUE) {
    metric = MetricKind.PER_SESSION_VALUE;
  } else if (metric === MetricKind.SIGNUPS) {
    metric = MetricKind.SIGNUPS_RATE;
  } else if (metric === MetricKind.SUBSCRIPTIONS) {
    metric = MetricKind.SUBSCRIPTION_RATE;
  } else if (metric === MetricKind.PROFIT) {
    metric = MetricKind.PROFIT_PER_SESSION;
  } else if (metric === MetricKind.RETURNS) {
    metric = MetricKind.RETURNS_PER_SESSION;
  }

  let controlMetric = getSingleMetric(data, metric, control?.id);
  let variationMetric = getSingleMetric(data, metric, variation?.id);
  const isInvalid =
    !controlMetric ||
    !variationMetric ||
    Number.isNaN(controlMetric + variationMetric);
  if (isInvalid) {
    return 0;
  }

  if (metric === MetricKind.SESSIONS) {
    variationMetric *= maybe(() => control.publishedChance || 1, 1);
    controlMetric *= maybe(() => variation.publishedChance || 1, 1);
  }
  const ratio = variationMetric / controlMetric;
  if (ratio === 1) {
    return 0;
  }
  return (ratio - 1) * 100;
}

export const getSingleMetric = (
  summary: SummaryResponse,
  metric: MetricKind,
  variant: string,
): number => {
  const metrics = summary?.variants?.filter((v) => v.variantId === variant);
  if (metrics && metrics.length > 0) {
    const actual = metrics[0].metrics.filter((m) => m.name === metric);
    if (actual && actual.length > 0) {
      return Number.isNaN(actual[0].value) ? 0 : actual[0].value;
    }
  }
  return 0;
};

export const getChanceToWin = (
  summary: SummaryResponse,
  variant: ExperienceVariant,
  primaryGoal: MetricKind,
): number => {
  const variantSummary = summary?.variants?.find(
    (v) => v.variantId === variant?.id,
  );
  const binomalChanceToWin =
    variantSummary?.conversionsBinomal?.chanceToWin ||
    summary?.conversionsBinomal?.chanceToWin;
  const gaussChanceToWin =
    variantSummary?.revenueGauss?.chanceToWin ||
    summary?.revenueGauss?.chanceToWin;
  const isGauss = MetricKind.AVG_ORDER_VALUE === primaryGoal;

  let chanceToWin = isGauss ? gaussChanceToWin : binomalChanceToWin;
  if (
    [
      MetricKind.PROFIT_PER_SESSION,
      MetricKind.PER_SESSION_VALUE,
      MetricKind.REVENUE,
      MetricKind.PROFIT,
    ].includes(primaryGoal)
  ) {
    chanceToWin = ((gaussChanceToWin || 0) + (binomalChanceToWin || 0)) / 2;
  }

  let chance: number;
  const isControl = variant?.name === CONTROL;
  if (isControl) {
    chance = 1 - (chanceToWin || 0);
  } else {
    chance = chanceToWin || 0;
  }

  return Math.round(chance * 100);
};

export function nanGuard(str: string) {
  return str.replace(`NaN`, `0`);
}

function formatNumber(num: number): string {
  if (num) {
    const str = num.toLocaleString(`en-US`, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return str.replace(`.00`, ``);
  }
  return `0`;
}
